import { Box, CircularProgress, Grid } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import ChevronIcon from 'common/shared-ui/src/icons/ChevronIcon';

import {
  editQuery,
  getAgile,
  getAgileFilters,
  getAgileRules,
  getSavedQuery,
  issueCanCreate,
  saveQuery,
  deleteSavedQuery,
} from '../../../../packages/common/api';
import WarningModal from '../../../../packages/common/shared-ui/src/components/WarningModal';
import { ButtonsTypes } from '../../../../packages/common/shared-ui/src/enums/commonEnums';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import {
  filterDateValues,
  formatFilters,
  formatQueryFilters,
  formatQueryToSave,
  formattedParamsData,
  paramsToQuery,
  queryToParams,
} from '../../../../packages/common/utils/gridFiltersHelperNew';
import CommonPage from '../../CommonPage/CommonPage';
import GridFiltersModalNew from '../../GridFiltersModalNew/GridFiltersModalNew';
import GridFiltersPanelNew from '../../GridFiltersPanelNew/GridFiltersPanelNew';
import GridSaveQueryModal from '../../GridSaveQueryModal/GridSaveQueryModal';
import AgileTable from '../AgileTable';

const AgileContainer = () => {
  const getLocalString = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const pageUseParams = useParams();
  const [openParamsModal, setOpenParamsModal] = useState(false);
  const [openSaveQueryModal, setOpenSaveQueryModal] = useState(false);
  const [queryData, setQueryData] = useState(null);
  const [query, setQuery] = useQueryParams({
    groups: StringParam,
    filters: StringParam,
    statuses: StringParam,
    fields: StringParam,
    indicators: StringParam,
    limit: NumberParam,
    offset: NumberParam,
  });
  const [currentParams, setCurrentParams] = useState(null);
  const [initialParams, setInitialParams] = useState(null);
  const [agileData, setAgileData] = useState(null);
  const [agileRules, setAgileRules] = useState({});
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [queryModalState, setQueryModalState] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [paramsLoading, setParamsLoading] = useState(true);
  const [canCreate, setCanCreate] = useState(false);

  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  //Получение фильтров
  useEffect(() => {
    let ignore = false;
    let params = {};
    pageUseParams.queryId && (params.query = Number(pageUseParams.queryId));
    pageUseParams.projectId && (params.project = pageUseParams.projectId);
    async function getParamsData() {
      const response = await getAgileFilters(params);
      if (!ignore) {
        return response;
      }
    }

    getParamsData()
      .then(res => {
        const { data: paramsData } = res;
        const params = formatFilters(paramsData);
        !initialParams && setInitialParams(params);
      })
      .catch(error => handleError(error.response));
  }, [pageUseParams.queryId, pageUseParams.projectId]);

  //Получение query по id
  useEffect(() => {
    setQueryData(null);
    let ignore = false;
    async function getSavedQueries() {
      const { data: response } = await getSavedQuery({
        id: pageUseParams.queryId,
      });
      if (!ignore) {
        return response;
      }
    }
    if (pageUseParams.queryId) {
      getSavedQueries()
        .then(res => {
          res && setQueryData(res);
        })
        .catch(error => handleError(error.response));
    }
  }, [handleError, pageUseParams.queryId]);

  useEffect(() => {
    if (!pageUseParams.queryId && initialParams) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams));
        setCurrentParams(initialParams);
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [initialParams, pageUseParams.queryId, setQuery]);
  useEffect(() => {
    if (pageUseParams.queryId && initialParams && queryData) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams, queryData.query));
        setCurrentParams(formatQueryFilters(initialParams, queryData.query));
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [
    initialParams,
    pageUseParams.queryId,
    setQuery,
    queryData,
    setCurrentParams,
  ]);

  useEffect(() => {
    let ignore = false;
    let params = {};
    pageUseParams.projectId && (params.project = pageUseParams.projectId);
    query.statuses !== undefined && (params.statuses = query.statuses);

    async function getRules() {
      const response = await getAgileRules(params);
      if (!ignore) {
        return response;
      }
    }

    getRules().then(res => {
      const { data: rules } = res || { rules: [] };
      setAgileRules(rules);
    });

    return () => {
      ignore = true;
    };
  }, [
    handleError,
    pageUseParams.projectId,
    pageUseParams.queryId,
    query.statuses,
  ]);

  useEffect(() => {
    setParamsLoading(true);
    let ignore = false;
    let params = {};
    setParamsLoading(true);
    let queryIsSet = false;
    queryIsSet =
      Object.values(query).filter(item => item !== undefined).length > 0;
    pageUseParams.projectId &&
      (params.project = Number(pageUseParams.projectId));
    queryIsSet && (params = { ...params, ...query });
    async function getAgileData() {
      const response = await getAgile(params);
      if (!ignore) {
        return response;
      }
    }
    if (queryIsSet) {
      getAgileData()
        .then(res => {
          const { data: agileData } = res;
          agileData.paging && setPagination(agileData.paging);
          setAgileData(agileData);
          setLoading(false);
          setParamsLoading(false);
        })
        .catch(error => handleError(error.response));
    }
    return () => {
      ignore = true;
    };
  }, [handleError, pageUseParams.projectId, query]);

  useEffect(() => {
    let ignore = false;
    async function getCanCreate() {
      let params = {};
      pageUseParams.projectId && (params.project = pageUseParams.projectId);
      const { data: response } = await issueCanCreate(params);
      if (!ignore) {
        return response;
      }
    }

    getCanCreate()
      .then(res => {
        setCanCreate(res);
      })
      .catch(error => handleError(error.response));

    return () => {
      ignore = true;
    };
  }, [pageUseParams.projectId, handleError]);

  const handleSelectFilters = useCallback(
    data => {
      if (!data.hasOwnProperty('operation')) {
        return;
      }
      const items = currentParams.filters.selectedItems.map(item => {
        if (item.fieldId === data.fieldId) {
          return data;
        } else {
          return item;
        }
      });
      const updatedParams = {
        ...currentParams,
        filters: { ...currentParams.filters, selectedItems: items },
      };
      setCurrentParams(updatedParams);
      setQuery(paramsToQuery(updatedParams));
    },
    [currentParams, setQuery],
  );

  const handleParamsModalSave = useCallback(
    data => {
      setCurrentParams(data);
      setQuery(paramsToQuery(data));
      setOpenParamsModal(false);
    },
    [setQuery],
  );

  const handleSaveQueryModalSave = useCallback(
    async modalData => {
      const value = formatQueryToSave(modalData, currentParams, 'agile');
      try {
        setSaveLoader(true);
        let response;
        if (queryModalState === 'edit') {
          response = await editQuery(pageUseParams.queryId, value);
        } else {
          response = await saveQuery(value);
        }
        if (response) {
          setSaveLoader(false);
          const { data: res } = response;
          if (pageUseParams.projectId) {
            window.open(
              `/projects/${pageUseParams.projectId}/agile/query/${res.id}`,
              '_self',
            );
          } else {
            window.open(`/agile/query/${res.id}`, '_self');
          }
        }
      } catch (error) {
        handleError(error.response);
      }
      setOpenSaveQueryModal(false);
    },
    [
      currentParams,
      handleError,
      pageUseParams.projectId,
      pageUseParams.queryId,
      queryModalState,
    ],
  );
  const handleParamsModalClose = useCallback(() => {
    setOpenParamsModal(false);
  }, []);
  const handleSaveQueryModalClose = useCallback(() => {
    setOpenSaveQueryModal(false);
  }, []);

  const handleWarningModalClose = useCallback(() => {
    setOpenWarningModal(false);
  }, []);

  const goCreateTask = useCallback(() => {
    let url = '/issues/create';
    let project = pageUseParams.projectId;
    if (project) {
      url = `/projects/${pageUseParams.projectId}/issues/create?projectId=${pageUseParams.projectId}`;
    }
    window.open(url);
  }, [pageUseParams.projectId]);

  const handleButtonEvent = useCallback(
    type => {
      switch (type) {
        case ButtonsTypes.ADD:
          goCreateTask();
          break;
        case ButtonsTypes.PARAMS:
          setOpenParamsModal(true);
          break;
        case ButtonsTypes.QUERY:
          setQueryModalState('create');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.EDIT_QUERY:
          setQueryModalState('edit');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.DELETE_QUERY:
          setOpenWarningModal(true);
          break;
      }
    },
    [goCreateTask],
  );
  const handleToggleDelete = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await deleteSavedQuery({ id: pageUseParams.queryId });
          navigate(`/projects/${pageUseParams.projectId}/agile`);
          setOpenWarningModal(false);
          break;
        case 'delete':
          setOpenWarningModal(false);
          break;
      }
    },
    [pageUseParams.queryId, navigate, pageUseParams.projectId],
  );

  const handleChangePagination = useCallback(
    data => {
      setQuery(prev => ({ ...prev, offset: data * 500 }));
    },
    [setQuery],
  );

  return (
    <>
      <WarningModal
        simple
        isWarningModalOpen={openWarningModal}
        handleToggle={handleToggleDelete}
        closeWarningModal={handleWarningModalClose}
        title={`${getLocalString('messages', 'deleteQuery')}?`}
      />
      {currentParams && (
        <GridFiltersModalNew
          handleSave={data => handleParamsModalSave(data)}
          handleClose={handleParamsModalClose}
          open={openParamsModal}
          data={currentParams}
          agile={true}
        />
      )}
      <GridSaveQueryModal
        state={queryModalState}
        saveLoader={saveLoader}
        permission={'canSaveAgileGlobalQueries'}
        queryData={queryData}
        open={openSaveQueryModal}
        handleSave={data => handleSaveQueryModalSave(data)}
        handleClose={handleSaveQueryModalClose}
      />
      <CommonPage
        loading={loading}
        buttonEvent={handleButtonEvent}
        queryName={queryData?.name}
        queryUserId={queryData?.userId}
        component={'AgileContainer'}
        canCreate={canCreate}
      >
        {currentParams && currentParams.filters?.selectedItems?.length > 0 && (
          <Box sx={{ marginTop: '10px' }}>
            <GridFiltersPanelNew
              data={currentParams.filters.selectedItems}
              handleCloseFilter={handleSelectFilters}
            ></GridFiltersPanelNew>
          </Box>
        )}
        {agileData && (
          <Box
            sx={{
              marginTop: '20px',
              overflow: 'hidden',
              display: 'flex',
              position: 'relative',
            }}
          >
            {paramsLoading && (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  zIndex: 99,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              >
                <CircularProgress color='secondary' />
              </Grid>
            )}
            <AgileTable
              key={query}
              agileData={agileData}
              agileRulesData={agileRules}
              projectId={pageUseParams.projectId}
              changePagination={handleChangePagination}
              paginationData={pagination}
              indicators={query.indicators}
            />
          </Box>
        )}
      </CommonPage>
    </>
  );
};

export default AgileContainer;
